export const LOCATION_AREA_ERROR =
  "Please use a general area like county/state or city/state, not a street address.";

const STREET_WORD_PATTERN =
  /\b(street|st|road|rd|avenue|ave|drive|dr|lane|ln|boulevard|blvd|court|ct|highway|hwy)\b/i;

export const looksLikeStreetAddress = (value = "") => {
  const text = String(value).trim();
  if (!text) return false;

  return /^\d+\s+/.test(text) || STREET_WORD_PATTERN.test(text);
};

export const validateBroadLocationInput = (value = "") => {
  if (looksLikeStreetAddress(value)) {
    return { ok: false, error: LOCATION_AREA_ERROR };
  }

  return { ok: true };
};

const getComponent = (components = [], type, nameKey = "long_name") =>
  components.find((component) => component.types?.includes(type))?.[nameKey] || "";

export const buildBroadLocationText = (locationData = {}) => {
  const county = locationData.county || "";
  const city = locationData.city || "";
  const state = locationData.state || "";

  if (county && state) return `${county}, ${state}`;
  if (county) return county;
  if (city && state) return `${city}, ${state}`;
  if (city) return city;

  return locationData.locationText || "";
};

export const parseBroadTypedLocation = (value = "") => {
  const text = String(value).trim();
  const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
  const first = parts[0] || text;
  const second = parts[1] || "";
  const third = parts[2] || "";
  const hasThreePartArea = Boolean(first && second && third);
  const countyPart = hasThreePartArea
    ? parts.find((part, index) => index > 0 && /\bcounty\b/i.test(part)) || second
    : first;
  const state = hasThreePartArea ? third : second;
  const isCounty = /\bcounty\b/i.test(first);

  return {
    city: isCounty ? "" : first,
    county: hasThreePartArea ? countyPart : isCounty ? first : "",
    state,
    locationText: buildBroadLocationText({
      city: isCounty ? "" : first,
      county: hasThreePartArea ? countyPart : isCounty ? first : "",
      state,
      locationText: text,
    }),
  };
};

export const buildBroadLocationFromGoogleResult = (result, fallbackText = "") => {
  const components = result?.address_components || [];
  const location = result?.geometry?.location;
  const lat = location?.lat?.();
  const lng = location?.lng?.();
  const types = result?.types || [];

  if (
    types.some((type) =>
      ["street_address", "route", "premise", "subpremise", "establishment"].includes(type)
    ) ||
    looksLikeStreetAddress(result?.formatted_address || fallbackText)
  ) {
    return null;
  }

  const county = getComponent(components, "administrative_area_level_2");
  const city =
    getComponent(components, "locality") ||
    getComponent(components, "postal_town") ||
    getComponent(components, "administrative_area_level_3");
  const state =
    getComponent(components, "administrative_area_level_1", "short_name") ||
    getComponent(components, "administrative_area_level_1");

  const parsedFallback = parseBroadTypedLocation(fallbackText);
  const broadLocation = {
    lat: typeof lat === "number" ? lat : null,
    lng: typeof lng === "number" ? lng : null,
    city: city || parsedFallback.city,
    county: county || parsedFallback.county,
    state: state || parsedFallback.state,
  };

  return {
    ...broadLocation,
    locationText: buildBroadLocationText({
      ...broadLocation,
      locationText: parsedFallback.locationText || fallbackText,
    }),
  };
};

export const normalizeBroadLocation = (locationData = {}, fallbackText = "") => {
  const parsedFallback = parseBroadTypedLocation(fallbackText || locationData.locationText);
  const normalized = {
    lat: typeof locationData.lat === "number" ? locationData.lat : null,
    lng: typeof locationData.lng === "number" ? locationData.lng : null,
    city: locationData.city || parsedFallback.city || "",
    county: locationData.county || parsedFallback.county || "",
    state: locationData.state || parsedFallback.state || "",
  };

  return {
    ...normalized,
    locationText: buildBroadLocationText({
      ...normalized,
      locationText: parsedFallback.locationText || fallbackText || locationData.locationText || "",
    }),
  };
};
